import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  FormControlLabel,
  Divider,
  Chip,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { PackageService } from '../../../api/packageService';
import { UnassignedMenu } from '../../../@type/package.types';

interface MenuAssignmentProps {
  selectedMenuIds: number[];
  onChange: (menuIds: number[]) => void;
  disabled?: boolean;
}

const MenuAssignment: React.FC<MenuAssignmentProps> = ({
  selectedMenuIds,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [unassignedMenus, setUnassignedMenus] = useState<UnassignedMenu[]>([]);

  useEffect(() => {
    loadUnassignedMenus();
  }, []);

  const loadUnassignedMenus = async () => {
    setLoading(true);
    try {
      const response = await PackageService.getUnassignedMenus();
      // Server returns: { success: true, code: "OK", data: [...menus] }
      if (response?.data?.data) {
        setUnassignedMenus(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load unassigned menus:', error);
      setUnassignedMenus([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMenu = (menuId: number) => {
    if (disabled) return;

    const currentIndex = selectedMenuIds.indexOf(menuId);
    const newSelectedIds = [...selectedMenuIds];

    if (currentIndex === -1) {
      newSelectedIds.push(menuId);
    } else {
      newSelectedIds.splice(currentIndex, 1);
    }

    onChange(newSelectedIds);
  };

  const handleToggleAll = () => {
    if (disabled) return;

    if (selectedMenuIds.length === unassignedMenus.length) {
      onChange([]);
    } else {
      onChange(unassignedMenus.map(menu => menu.id));
    }
  };

  const allSelected = unassignedMenus.length > 0 && selectedMenuIds.length === unassignedMenus.length;
  const someSelected = selectedMenuIds.length > 0 && selectedMenuIds.length < unassignedMenus.length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper variant="outlined" className="p-4">
      <Box mb={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" className="font-semibold">
            {t('packages.menuAssignment')}
          </Typography>
          <Chip
            label={`${selectedMenuIds.length} ${t('packages.selected')}`}
            color={selectedMenuIds.length > 0 ? 'primary' : 'default'}
            size="small"
          />
        </Box>

        {unassignedMenus.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              {t('packages.noUnassignedMenus')}
            </Typography>
            <Typography variant="caption" color="text.secondary" mt={1}>
              {t('packages.allMenusAssigned')}
            </Typography>
          </Box>
        ) : (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleToggleAll}
                  disabled={disabled}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" className="font-semibold">
                  {allSelected ? t('packages.deselectAll') : t('packages.selectAll')}
                </Typography>
              }
            />

            <Divider className="my-2" />

            <List dense>
              {unassignedMenus.map((menu) => {
                const isSelected = selectedMenuIds.includes(menu.id);
                return (
                  <ListItem
                    key={menu.id}
                    disablePadding
                    className={`rounded-md ${isSelected ? 'bg-blue-50' : ''}`}
                  >
                    <ListItemButton
                      onClick={() => handleToggleMenu(menu.id)}
                      disabled={disabled}
                      className="hover:bg-gray-50"
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={isSelected}
                          tabIndex={-1}
                          disableRipple
                          disabled={disabled}
                          color="primary"
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body1">{menu.name}</Typography>
                            {isSelected && (
                              <CheckCircleIcon fontSize="small" color="primary" />
                            )}
                          </Box>
                        }
                        secondary={`Code: ${menu.code}`}
                      />
                      <Chip
                        label={menu.is_active ? t('common.active') : t('common.inactive')}
                        size="small"
                        color={menu.is_active ? 'success' : 'default'}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </>
        )}
      </Box>

      {selectedMenuIds.length > 0 && (
        <Box mt={2} p={2} bgcolor="blue.50" borderRadius={1}>
          <Typography variant="caption" color="primary">
            💡 {t('packages.menuAssignmentNote')}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default MenuAssignment;
